import {
  isPresentableChatWidget,
  isRenderableChatMessage,
} from "@/lib/agent/renderableChatMessage";
import {
  getActiveWidgetSurfacePresets,
  getRendererPresentedMessagePresets,
  getRendererPresentedWidgetPresets,
  isFrontReviewDecisionPresetSurface,
} from "@/lib/agent/visiblePresetCapabilities";
import { isDesignerRenderActionId } from "@/lib/agent/designerActionPresentation";
import { getWidgetPresets } from "@/lib/agent/widgetPresets";
import { isWorkspaceSecondaryAction } from "@/lib/designer/workspaceActions";
import type { AgentMessage, PresetOption } from "@/types/createAgent";

export interface MobileAttentionContent {
  messageId: string;
  headline: string;
  detail: string | null;
  hasActions: boolean;
  actionCount: number;
}

function compactText(value: string | undefined, limit: number): string | null {
  const normalized = value?.replace(/\s+/g, " ").trim();
  if (!normalized) return null;
  if (normalized.length <= limit) return normalized;
  const clipped = normalized.slice(0, limit - 1).trimEnd();
  const lastSpace = clipped.lastIndexOf(" ");
  return `${lastSpace > limit / 2 ? clipped.slice(0, lastSpace) : clipped}…`;
}

function belongsInSharedChat(preset: PresetOption, widgetId?: string): boolean {
  return (
    !isDesignerRenderActionId(preset.actionId) &&
    (!isWorkspaceSecondaryAction(preset.actionId) ||
      (Boolean(widgetId) && widgetId !== "v2-review-action-panel-standing"))
  );
}

/**
 * Summarizes the latest completed, visible designer turn for the composer panel.
 * Draft text and global presets are intentionally absent: a new submitted user
 * turn supersedes the preceding assistant's options until its own reply lands.
 */
export function projectMobileAttentionContent({
  messages,
  busy,
}: {
  messages: readonly AgentMessage[];
  busy: boolean;
}): MobileAttentionContent | null {
  if (busy) return null;

  let latestAssistantIndex = -1;
  let latestUserIndex = -1;
  for (let index = 0; index < messages.length; index += 1) {
    const message = messages[index];
    if (message.role === "user") latestUserIndex = index;
    else if (isRenderableChatMessage(message)) latestAssistantIndex = index;
  }
  if (latestAssistantIndex < 0 || latestUserIndex > latestAssistantIndex)
    return null;

  const message = messages[latestAssistantIndex];
  const presentableWidgets = (message.widgets ?? []).filter(
    isPresentableChatWidget
  );
  const isFrontReviewDecision = isFrontReviewDecisionPresetSurface(
    presentableWidgets.length > 0
      ? getActiveWidgetSurfacePresets(presentableWidgets)
      : (message.presets ?? [])
  );
  if (isFrontReviewDecision) {
    // The chat renderer initially shows the review launchers. Back styles and
    // front changes appear only after the customer opens their panel.
    return {
      messageId: message.id,
      headline: "Choose your next step",
      detail: compactText(message.content, 240),
      hasActions: true,
      actionCount: 2,
    };
  }
  const widgetActions = presentableWidgets.map(widget => ({
    widget,
    actions: getRendererPresentedWidgetPresets(widget).filter(preset =>
      belongsInSharedChat(preset, widget.id)
    ),
  }));
  const actions = presentableWidgets.length
    ? widgetActions.flatMap(entry => entry.actions)
    : getRendererPresentedMessagePresets(message.presets ?? []).filter(preset =>
        belongsInSharedChat(preset)
      );
  const firstWidget =
    widgetActions.find(
      entry => entry.actions.length > 0 && entry.widget.title?.trim()
    )?.widget ??
    widgetActions.find(
      entry => entry.actions.length > 0 && entry.widget.summary?.trim()
    )?.widget ??
    presentableWidgets.find(
      widget =>
        getWidgetPresets(widget).length === 0 &&
        (widget.title?.trim() || widget.summary?.trim())
    );
  const detail =
    compactText(message.content, 240) ?? compactText(firstWidget?.summary, 240);
  const actionCount = actions.length;

  // An image-only message carries no useful composer guidance. The workspace
  // can show its saved-preview milestone instead.
  if (!detail && !firstWidget?.title?.trim() && actionCount === 0) return null;

  return {
    messageId: message.id,
    headline:
      compactText(firstWidget?.title, 80) ??
      (actionCount > 0 ? "Choose your next step" : "Your designer replied"),
    detail,
    hasActions: actionCount > 0,
    actionCount,
  };
}
